import React from 'react';
import PropTypes from 'prop-types';
import './PublicShopSettingsPanel.scss';
import { confirmAlert } from 'react-confirm-alert';
import cn from 'classnames';

import CheckboxButton from '../../../General/Buttons/CheckboxButton';

import { getSettings, getUserTier, isAdmin } from '../../../../Helpers/user_helpers';

const PublicShopSettingsPanel = props => {
  const { user } = props;
  const { isHiddenFromPublicShop, productsVisibleInShop, showConsultsTabInShop, hideTrustCountFromShop } = getSettings(user) || {};

  const toggleUserSetting = async (setting, value) => {
    await props.updateUserSettings({ [setting]: value });
    await props.syncCurrentUser();
  };

  let sections = [];

  /*
    Searchable in ShopMy.

    Specify whether the creator wants to show up on the publicly searchable ShopMy website.
  */
  const canEnableSearch = getUserTier(user).bestTier <= 2 || isAdmin(user);

  sections.push({
    title: 'Visibility Settings',
    subtitle: <>Specify how you want to show up on the publicly searchable ShopMy website.</>,
    isHighlighted: window.location.hash === '#publicshopconfig',
    options: [
      {
        title: 'Discoverable on ShopMy',
        isDisabled: !canEnableSearch,
        value: canEnableSearch && !isHiddenFromPublicShop,
        onChange: () => {
          if (!canEnableSearch) return window.ALERT.warn(`You must reach the trendsetter status to be findable via search.`);

          toggleUserSetting('isHiddenFromPublicShop', !isHiddenFromPublicShop);
          if (!isHiddenFromPublicShop) window.ALERT.info('Your shop will no longer be findable via search within 24 hours.');
          else window.ALERT.success('Your shop will be findable via search within 24 hours');
        }
      }
    ]
  });

  /*
    Visible Products.

    Specify which products you want to show up on your "Popular" and "Latest" tabs.

      Option 1: All Products
      Option 2: Only Products I've Added to Visible Collections
  */
  const confirmAndCommitVisibilityChange = newValue => {
    confirmAlert({
      title: 'Just confirming.',
      message: {
        all: 'This will show all links regardless of visibility settings.',
        'visible-collections-and-quick-links': 'This will show products that are in visible collections and quick links.',
        'visible-collections-and-popular-quick-links':
          'This will show products that are in visible collections and quick links that have driven over 10 clicks. Quick link visibility with popularity may take up to 24 hours to show up.',
        'visible-collections': 'This will show products that are in collections and hide all quick links.'
      }[newValue],
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Make Change',
          onClick: () => {
            toggleUserSetting('productsVisibleInShop', newValue);
            window.ALERT.info(
              {
                all: 'All links that are connected to popular products are now visible.',
                'visible-collections-and-quick-links': 'Successfully hid products that are not in visible collections and quick links.',
                'visible-collections-and-popular-quick-links':
                  'Successfully hid products that are not in collections or do not reach the 10 click threshold.',
                'visible-collections': 'Successfully hid products that are not in visible collections.'
              }[newValue] || 'Successfully updated visibility settings.'
            );
          }
        }
      ]
    });
  };

  sections.push({
    title: 'Visible Products',
    subtitle: <>Specify which products you want to show up on your "Popular" and "Latest" tabs.</>,
    isHighlighted: window.location.hash === '#visibleproducts',
    options: [
      {
        title: 'Visible Collections and Quick Links (Recommended)',
        tooltip: 'This option will show products that are in collections and quick links.',
        value: productsVisibleInShop === 'visible-collections-and-quick-links',
        onChange: () => confirmAndCommitVisibilityChange('visible-collections-and-quick-links')
      },
      {
        title: 'Visible Collections and Popular Quick Links',
        tooltip:
          'This option will show products that are in collections and quick links that have driven over 10 clicks. Quick link visibility with popularity may take up to 24 hours to show up.',
        value: productsVisibleInShop === 'visible-collections-and-popular-quick-links',
        onChange: () => confirmAndCommitVisibilityChange('visible-collections-and-popular-quick-links')
      },
      {
        title: 'Visible Collections Only',
        tooltip: 'This option will show products that are in collections and hide all quick links.',
        value: productsVisibleInShop === 'visible-collections',
        onChange: () => confirmAndCommitVisibilityChange('visible-collections')
      },
      {
        title: 'All Links',
        tooltip: 'This option will show all links regardless of visibility settings.',
        value: productsVisibleInShop === 'all',
        onChange: () => confirmAndCommitVisibilityChange('all')
      }
    ]
  });

  /*
    Consults Visibility

    Specify whether you want to show the Consults tab on your shop page.
  */
  sections.push({
    title: 'Offering Consultations',
    subtitle: (
      <>
        Specify whether you want to offer Consults on your shop page. Learn more{' '}
        <a
          href='https://guide.shopmy.us/building-your-shop/43V9gf7Co5VBrmboNBWQ1S/what-are-consults/43V9gf7Co5yf1B5R9LFRHF'
          target='_blank'
          rel='noopener noreferrer'
        >
          here
        </a>
        .
      </>
    ),
    options: [
      {
        title: 'Show Consults Tab',
        value: !!showConsultsTabInShop,
        onChange: () => toggleUserSetting('showConsultsTabInShop', !showConsultsTabInShop)
      },
      {
        title: 'Hide Consults Tab',
        value: !showConsultsTabInShop,
        onChange: () => toggleUserSetting('showConsultsTabInShop', !showConsultsTabInShop)
      }
    ]
  });

  /*
    Trusted By Count

    Specify whether you want to show the "Trusted By" count on your shop.
  */
  sections.push({
    title: 'Trusted By Count',
    subtitle: 'Specify whether you want to show the "Trusted By" count on your shop.',
    options: [
      {
        title: 'Show Trusted By Count',
        value: !hideTrustCountFromShop,
        onChange: () => toggleUserSetting('hideTrustCountFromShop', !hideTrustCountFromShop)
      },
      {
        title: 'Hide Trusted By Count',
        value: !!hideTrustCountFromShop,
        onChange: () => toggleUserSetting('hideTrustCountFromShop', !hideTrustCountFromShop)
      }
    ]
  });

  return (
    <div className='location-data-panel-outer'>
      <div className='location-data-panel-inner'>
        {sections.map(section => {
          const { title, subtitle, isHighlighted, options } = section;

          return (
            <div className='settings-section' key={`location-panel-${title}`}>
              {title && (
                <div
                  className={cn('settings-section-title', {
                    highlighted: isHighlighted
                  })}
                >
                  {title}
                </div>
              )}
              {subtitle && <div className='settings-section-subtitle'>{subtitle}</div>}

              <div className='settings-section-list'>
                {options.map(option => {
                  const { title, userSettingsVariable, value } = option;
                  return (
                    <CheckboxButton
                      key={`location-panel-${userSettingsVariable}`}
                      isChecked={value}
                      isDisabled={option.isDisabled}
                      onChange={option.onChange}
                      text={title}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

PublicShopSettingsPanel.propTypes = {
  user: PropTypes.object.isRequired,
  updateUserSettings: PropTypes.func.isRequired
};

export default PublicShopSettingsPanel;
